import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  Snackbar,
  Slide,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

function ProductSearch({ searchResults }) {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openDialog, setOpenDialog] = useState(false); // State to control the dialog visibility
  const [openSnackbar, setOpenSnackbar] = useState(false); // Snackbar open state
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Snackbar message

  const handleCardClick = (product) => {
    setSelectedProduct(product);
    setOpenDialog(true); // Open the dialog on card click
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProduct(null); // Reset selected product when dialog is closed
  };

  // Function to copy text to clipboard
  const handleCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setSnackbarMessage("Copied to clipboard!");
        setOpenSnackbar(true);
        setTimeout(() => {
          setOpenSnackbar(false); // Close Snackbar after 2 seconds
        }, 2000);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  const handleImgDownload = async (imageUrl, fileName) => {
    try {
      // Fetch the image as a Blob
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // Create a new Blob URL for the file
      const url = URL.createObjectURL(blob);

      // Create an anchor tag and set the download attributes
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || imageUrl.split("/").pop().split("?")[0]; // Use the provided file name or the default from the URL

      // Programmatically click the link to trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link); // Clean up by removing the link

      // Revoke the Blob URL after use to release resources
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the image:", error);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ marginBottom: 2 }}>
        Search Results
      </Typography>

      {/* Use a flexbox layout with product name, SKU on the left, and image on the right */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {searchResults.length > 0 ? (
          searchResults.map((product) => (
            <Card
              key={product.id}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                maxWidth: 600,
                transition: "transform 0.3s ease", // Smooth transition for the hover effect
                "&:hover": {
                  transform: "translateY(-5px)", // Moves the card up by 5px on hover
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)", // Optional: Adds a shadow effect on hover
                },
              }}
              onClick={() => handleCardClick(product)} // Handle click to open dialog
            >
              <CardContent sx={{ flex: 1 }}>
                {/* Product Name and SKU in a vertical stack */}
                <Typography variant="h6">{product.productName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  SKU: {product.sku}
                </Typography>
              </CardContent>

              {/* Product Image on the right */}
              <div>
                {Array.isArray(product.images) && product.images.length > 0 ? (
                  <img src={product.images[0]} alt={`${product.productName}`} style={{ width: "140px", height: "auto", marginLeft: "10px" }} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No image available
                  </Typography>
                )}
              </div>
            </Card>
          ))
        ) : (
          <Typography>No products found for this category.</Typography>
        )}
      </Box>

      {/* Dialog for showing product details */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth={true} maxWidth="lg">
        <DialogTitle>
          {selectedProduct?.productName}
          <IconButton
            sx={{ marginLeft: 1 }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent dialog close when copying
              handleCopy(selectedProduct?.productName);
            }}
          >
            <ContentCopyIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            SKU: {selectedProduct?.sku}
            <IconButton
              sx={{ marginLeft: 1 }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent dialog close when copying
                handleCopy(selectedProduct?.sku);
              }}
            >
              <ContentCopyIcon />
            </IconButton>
          </Typography>
          <Typography variant="body2" sx={{ marginTop: 1 }}>
            {selectedProduct?.description}
            <IconButton
              sx={{ marginLeft: 1 }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent dialog close when copying
                handleCopy(selectedProduct?.description);
              }}
            >
              <ContentCopyIcon />
            </IconButton>
          </Typography>
          {selectedProduct?.images && selectedProduct.images.length > 0 ? (
            <Box sx={{ marginTop: 2 }}>
              {selectedProduct.images.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "inline-block",
                    textAlign: "center",
                    marginRight: "10px",
                    marginBottom: "10px",
                  }}
                >
                  <img src={image} alt={`${selectedProduct.productName} ${index + 1}`} style={{ width: "200px", height: "auto" }} />
                  <Box>
                    {/* Trigger download via the handleImgDownload function */}
                    <Button variant="outlined" onClick={() => handleImgDownload(image, `${selectedProduct.productName}_${index + 1}.jpg`)}>
                      Download
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No images available
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for copy feedback */}
      <Snackbar
        open={openSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        autoHideDuration={2000} // Automatically close after 2 seconds
        onClose={() => setOpenSnackbar(false)} // Close the Snackbar
        TransitionComponent={(props) => <Slide {...props} direction="down" />} // Slide down transition
        message={snackbarMessage}
      />
    </Box>
  );
}

export default ProductSearch;
